import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import {
  AddressType,
  enumFreightTerm,
  enumPaymentTerm,
  FreightTerm,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import {
  Checkbox,
  DatePicker,
  type DateValue,
  Label,
  Select,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import { BookingMultiSelect } from '../_shared/BookingMultiSelect';

import { AccountAddress } from '~/modules/trade/_graphql/queryAccountAddresses';
import * as state from '~/modules/trade/_state/salesOrderForm.state';
import {
  $salesOrderForm,
  $updateSalesOrderForm,
} from '~/modules/trade/_state/salesOrderForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectContact } from '~/shared/components/select-fields/SelectContact';
import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectUser } from '~/shared/components/select-fields/SelectUser';

export type Props = {
  disabled?: boolean;
};

const formatAddressDisplay = (
  address: Omit<AccountAddress, 'id' | 'account'>,
) => {
  const parts = [
    address.streetAddress,
    address.city,
    address.state,
    address.postalCode,
  ].filter(Boolean);
  return parts.join(', ') || 'Address';
};

export const SalesOrderWizardStepContractInfo = ({
  disabled = false,
}: Props) => {
  const t = useTranslations();
  const [form] = useAtom($salesOrderForm);
  const [, updateForm] = useAtom($updateSalesOrderForm);

  const hasSelectedCustomer = !!form.customerId;

  // Sync bill-to with ship-to when "same as ship" is checked
  React.useEffect(() => {
    if (form.sameAsShip && form.shipToLocationId && form.shippingAddress) {
      updateForm({
        billToLocationId: form.shipToLocationId,
        billingAddress: form.shippingAddress,
      });
    }
  }, [
    form.sameAsShip,
    form.shipToLocationId,
    form.shippingAddress,
    updateForm,
  ]);

  // Fetch account data when customerId changes
  const { data: accountsQueryData, fetch: fetchAccountsQuery } = useAtomValue(
    state.$accountsByIdQueryLazy,
  );

  React.useEffect(() => {
    if (form.customerId) {
      fetchAccountsQuery({
        accountIds: [form.customerId],
      });
    }
  }, [form.customerId, fetchAccountsQuery]);

  const selectedAccount = React.useMemo(
    () => accountsQueryData?.find((account) => account.id === form.customerId),
    [accountsQueryData, form.customerId],
  );
  const selectedAccountAddresses = React.useMemo(
    () => selectedAccount?.addresses ?? [],
    [selectedAccount],
  );

  // Memoized options for ComboBox components
  const shipToAddressOptions = React.useMemo(
    () =>
      selectedAccountAddresses?.map((address) => ({
        key: address.id,
        label: `${address.name ?? address.address.addressType} - ${formatAddressDisplay(address.address)}`,
      })) ?? [],
    [selectedAccountAddresses],
  );

  const billToAddressOptions = React.useMemo(
    () =>
      selectedAccountAddresses?.map((address) => ({
        key: address.id,
        label: `${address.name ?? address.address.addressType} - ${formatAddressDisplay(address.address)}`,
      })) ?? [],
    [selectedAccountAddresses],
  );

  const handleFacilityChange = React.useCallback(
    (facilityId: string | undefined) => {
      updateForm({ facilityId: facilityId ?? null });
    },
    [updateForm],
  );

  const handleStartDateChange = React.useCallback(
    (date: DateValue | null) => {
      updateForm({ salesOrderStartDate: date as CalendarDate | null });
    },
    [updateForm],
  );

  const handleEndDateChange = React.useCallback(
    (date: DateValue | null) => {
      updateForm({ salesOrderEndDate: date as CalendarDate | null });
    },
    [updateForm],
  );

  const handleFieldChange = React.useCallback(
    <K extends keyof typeof form>(field: K) =>
      (value: (typeof form)[K]) => {
        updateForm({ [field]: value });
      },
    [updateForm],
  );

  const handleSelectChange = React.useCallback(
    (field: string, value: string | null | undefined) => {
      updateForm({ [field]: value });
    },
    [updateForm],
  );

  const handleCheckboxChange = React.useCallback(
    (field: string, checked: boolean) => {
      updateForm({ [field]: checked });
    },
    [updateForm],
  );

  const handleCustomerChange = React.useCallback(
    (value: string | null | undefined) => {
      const customerId = value ?? null;
      updateForm({
        customerId,
        // Clear dependent fields when customer changes
        contactId: null,
        shipToLocationId: null,
        billToLocationId: null,
        shippingAddress: null,
        billingAddress: null,
      });
    },
    [updateForm],
  );

  const handleShipToAddressChange = React.useCallback(
    (addressId: string) => {
      const selectedAddress = selectedAccountAddresses?.find(
        (addr) => addr.id === addressId,
      );
      if (selectedAddress) {
        updateForm({
          shipToLocationId: addressId as string,
          shippingAddress: {
            streetAddress: selectedAddress.address.streetAddress || '',
            city: selectedAddress.address.city || '',
            state: selectedAddress.address.state || '',
            postalCode: selectedAddress.address.postalCode || '',
            country: selectedAddress.address.country || '',
            addressType: selectedAddress.address.addressType as AddressType,
            isMailingAddress: true,
          },
        });
      }
    },
    [updateForm, selectedAccountAddresses],
  );

  const handleBillToAddressChange = React.useCallback(
    (addressId: string) => {
      const selectedAddress = selectedAccountAddresses?.find(
        (addr) => addr.id === addressId,
      );
      if (selectedAddress) {
        updateForm({
          billToLocationId: addressId as string,
          billingAddress: {
            streetAddress: selectedAddress.address.streetAddress || '',
            city: selectedAddress.address.city || '',
            state: selectedAddress.address.state || '',
            postalCode: selectedAddress.address.postalCode || '',
            country: selectedAddress.address.country || '',
            addressType: selectedAddress.address.addressType as AddressType,
            isMailingAddress: true,
          },
        });
      }
    },
    [updateForm, selectedAccountAddresses],
  );

  return (
    <div className="@SalesOrderWizardStepContractInfo bg-white">
      <div className="flex flex-col gap-4">
        {/* Contract Info Section */}
        <div className="flex flex-col gap-4 border-b border-neutral/30 pb-6">
          <h3 className="text-lg font-semibold text-neutral/90">
            {t.salesOrdersView.wizard.contractInfo.contractInfo}
          </h3>
          <div className="grid grid-cols-4 items-start gap-4">
            <TextField
              label={t.salesOrdersView.wizard.contractInfo.salesOrderNumber}
              value={form.salesOrderNumber}
              onChange={handleFieldChange('salesOrderNumber')}
              placeholder={t.common.placeholders.generateNumber}
              isRequired
              isDisabled={disabled}
            />
            <BookingMultiSelect
              onSelectionChange={handleFieldChange('bookings')}
              selectedEntries={form.bookings}
              isDisabled={disabled}
            />
            <SelectFacility
              value={form.facilityId}
              onChange={handleFacilityChange}
              label={t.salesOrdersView.wizard.contractInfo.facility}
              autoSelectFirstOption
              isRequired
              isDisabled={disabled}
            />
          </div>
          <div className="grid grid-cols-4 items-end gap-4">
            <DatePicker
              value={form.salesOrderStartDate as DateValue | null}
              onChange={handleStartDateChange}
              label={t.salesOrdersView.wizard.contractInfo.salesOrderStartDate}
              isRequired
              isDisabled={disabled}
            />
            <DatePicker
              value={form.salesOrderEndDate as DateValue | null}
              onChange={handleEndDateChange}
              label={t.salesOrdersView.wizard.contractInfo.salesOrderEndDate}
              isDisabled={disabled}
            />
            <SelectUser
              value={form.accountRepresentativeId ?? undefined}
              placeholder={
                t.salesOrdersView.wizard.placeholders.accountRepresentative
              }
              onChange={(value: string | undefined) =>
                handleSelectChange('accountRepresentativeId', value ?? null)
              }
              label={
                t.salesOrdersView.wizard.contractInfo.accountRepresentative
              }
              isDisabled={disabled}
            />
            <TextField
              value={form.counterpartyPurchaseOrderNumber}
              onChange={handleFieldChange('counterpartyPurchaseOrderNumber')}
              label={
                t.salesOrdersView.wizard.contractInfo
                  .counterpartyPurchaseOrderNumber
              }
              placeholder={
                t.salesOrdersView.wizard.placeholders
                  .counterpartyPurchaseOrderNumber
              }
              isDisabled={disabled}
            />
            <div className="flex h-9.5 items-center rounded-lg border border-neutral/40 bg-white px-3 py-2">
              <Checkbox
                isSelected={form.doNotShip}
                onChange={(checked) =>
                  handleCheckboxChange('doNotShip', checked)
                }
                className="mr-3"
                isDisabled={disabled}
              >
                <span className="text-sm text-neutral/90">
                  {t.salesOrdersView.wizard.contractInfo.doNotShip}
                </span>
              </Checkbox>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="border-b border-neutral/30 pb-6">
          <h3 className="mb-4 text-lg font-semibold text-neutral/90">
            {t.salesOrdersView.wizard.contractInfo.customerInformation}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <SelectAccount
              value={form.customerId}
              onChange={(value) => handleCustomerChange(value)}
              label={t.salesOrdersView.wizard.contractInfo.customer}
              isRequired
              placeholder={t.salesOrdersView.wizard.placeholders.customer}
              isDisabled={disabled}
            />
            <SelectContact
              value={form.contactId}
              onChange={(value) => handleSelectChange('contactId', value)}
              label={t.salesOrdersView.wizard.contractInfo.contact}
              accountIds={form.customerId ? [form.customerId] : []}
              isDisabled={!hasSelectedCustomer || disabled}
            />

            <Select
              label={t.salesOrdersView.wizard.contractInfo.shipToLocation}
              options={shipToAddressOptions}
              value={form.shipToLocationId}
              onChange={handleShipToAddressChange}
              placeholder={
                hasSelectedCustomer
                  ? t.salesOrdersView.wizard.placeholders.location
                  : t.salesOrdersView.wizard.placeholders.selectCustomerFirst
              }
              isDisabled={!hasSelectedCustomer || disabled}
              isRequired
            />

            <div className="space-y-2">
              <Select
                label={t.salesOrdersView.wizard.contractInfo.billToLocation}
                options={billToAddressOptions}
                value={
                  form.sameAsShip
                    ? form.shipToLocationId
                    : form.billToLocationId
                }
                onChange={handleBillToAddressChange}
                placeholder={
                  hasSelectedCustomer
                    ? t.salesOrdersView.wizard.placeholders.location
                    : t.salesOrdersView.wizard.placeholders.selectCustomerFirst
                }
                isDisabled={!hasSelectedCustomer || form.sameAsShip || disabled}
                isRequired
              />
              <Checkbox
                isSelected={form.sameAsShip}
                onChange={(checked) => {
                  handleCheckboxChange('sameAsShip', checked);
                  if (checked) {
                    updateForm({
                      billToLocationId: form.shipToLocationId,
                      billingAddress: form.shippingAddress,
                    });
                  }
                }}
                isDisabled={disabled}
              >
                {t.salesOrdersView.wizard.contractInfo.sameAsShip}
              </Checkbox>
            </div>
          </div>
        </div>

        {/* Payment Info */}
        <div className="border-b border-neutral/30 pb-6">
          <h3 className="mb-4 text-lg font-semibold text-neutral/90">
            {t.salesOrdersView.wizard.contractInfo.paymentInfo}
          </h3>

          <div className="grid grid-cols-3 gap-4">
            <SelectCurrency
              value={form.paymentCurrency}
              onChange={(value) => handleSelectChange('paymentCurrency', value)}
              label={t.salesOrdersView.wizard.contractInfo.paymentCurrency}
              isRequired
              isDisabled={disabled}
            />
            <SelectEnum<PaymentTerm>
              enumValue={enumPaymentTerm}
              isRequired
              value={form.paymentTerm ?? undefined}
              onChange={(value) =>
                handleSelectChange('paymentTerm', value as string | null)
              }
              label={t.salesOrdersView.wizard.contractInfo.paymentTerm}
              isDisabled={disabled}
            />
            <SelectEnum<FreightTerm>
              enumValue={enumFreightTerm}
              isRequired
              value={form.freightTerm}
              onChange={(value) => handleSelectChange('freightTerm', value)}
              label={t.salesOrdersView.wizard.contractInfo.freightTerm}
              isDisabled={disabled}
            />
          </div>
        </div>

        {/* Other Info */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral/90">
            {t.salesOrdersView.wizard.contractInfo.otherInfo}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1 block text-sm font-medium text-neutral/70">
                {t.salesOrdersView.wizard.contractInfo.internalNotes}
              </Label>
              <TextArea
                value={form.internalNotes}
                onChange={handleFieldChange('internalNotes')}
                placeholder="Add any internal notes (not visible on documents)..."
                rows={3}
                isDisabled={disabled}
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm font-medium text-neutral/70">
                {t.salesOrdersView.wizard.contractInfo.externalNotes}
              </Label>
              <TextArea
                value={form.externalNotes}
                onChange={handleFieldChange('externalNotes')}
                placeholder="Add any external notes (visible on documents)..."
                rows={3}
                isDisabled={disabled}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
